import { useROIStore } from '../store'
import { formatCurrency, formatNumber } from '../data'
import { clsx } from 'clsx'

export function ROIAnalysis() {
  const { regions, aggregatedResults, getRegionCalculation } = useROIStore()

  if (!aggregatedResults) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
        <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <span>📈</span> ROI Analysis
        </h3>
        <p className="text-sm text-[var(--text-muted)] text-center py-4">
          Add products to your verification pipeline to see ROI analysis.
        </p>
      </div>
    )
  }

  const {
    avgImprovement,
    totalAdditionalConversions,
    totalMonthlyValue,
    totalAnnualValue,
    totalCurrentConversions,
    totalProjectedConversions,
    totalVolume,
  } = aggregatedResults

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 space-y-4">
      <h3 className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
        <span>📈</span> ROI Analysis
      </h3>

      {/* Region Breakdown (multi-region only) */}
      {regions.length > 1 && (
        <div className="bg-[var(--bg-tertiary)] rounded-lg p-3">
          <h4 className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-2">Region Breakdown</h4>
          <div className="space-y-2">
            {regions.map((region) => {
              const calc = getRegionCalculation(region.id)
              if (!calc) return null

              return (
                <div key={region.id} className="flex items-center justify-between text-xs">
                  <span className="text-[var(--text-primary)]">{region.name || region.country}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-[var(--text-secondary)]">
                      {region.passRate.toFixed(1)}% → {calc.newRate.toFixed(1)}%
                    </span>
                    <span className={clsx('font-medium', calc.improvement > 0 ? 'text-success' : 'text-[var(--text-secondary)]')}>
                      +{calc.improvement.toFixed(1)}%
                    </span>
                    <span className="text-[var(--text-primary)] w-20 text-right">
                      {formatCurrency(calc.monthlyValue)}/mo
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[var(--bg-tertiary)] rounded-lg p-3">
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide mb-1">
            Avg Pass Rate Improvement
          </div>
          <div className={clsx('text-xl font-bold', avgImprovement > 0 ? 'text-success' : 'text-[var(--text-primary)]')}>
            +{avgImprovement.toFixed(1)}%
          </div>
        </div>

        <div className="bg-[var(--bg-tertiary)] rounded-lg p-3">
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide mb-1">
            Additional Conversions/Month
          </div>
          <div className={clsx('text-xl font-bold', totalAdditionalConversions > 0 ? 'text-success' : 'text-[var(--text-primary)]')}>
            +{formatNumber(Math.round(totalAdditionalConversions))}
          </div>
        </div>

        <div className="bg-[var(--bg-tertiary)] rounded-lg p-3">
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide mb-1">
            Monthly Value Added
          </div>
          <div className={clsx('text-xl font-bold', totalMonthlyValue > 0 ? 'text-primary' : 'text-[var(--text-primary)]')}>
            {formatCurrency(totalMonthlyValue)}
          </div>
        </div>

        <div className="bg-[var(--bg-tertiary)] rounded-lg p-3">
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide mb-1">
            Annual Value Added
          </div>
          <div className={clsx('text-xl font-bold', totalAnnualValue > 0 ? 'text-primary' : 'text-[var(--text-primary)]')}>
            {formatCurrency(totalAnnualValue)}
          </div>
        </div>
      </div>

      {/* Calculation Breakdown */}
      <div className="border-t border-[var(--border-color)] pt-3">
        <h4 className="text-xs text-[var(--text-muted)] uppercase tracking-wide mb-2">Calculation Breakdown</h4>
        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-[var(--text-secondary)]">Total current conversions:</span>
            <span className="text-[var(--text-primary)]">{Math.round(totalCurrentConversions).toLocaleString()} / month</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[var(--text-secondary)]">Total projected conversions:</span>
            <span className="text-[var(--text-primary)]">{Math.round(totalProjectedConversions).toLocaleString()} / month</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[var(--text-secondary)]">Total additional conversions:</span>
            <span className="text-success">+{Math.round(totalAdditionalConversions).toLocaleString()} / month</span>
          </div>
          <div className="flex justify-between pt-1 border-t border-[var(--border-color)] mt-1">
            <span className="text-[var(--text-secondary)]">Total monthly volume:</span>
            <span className="text-[var(--text-primary)]">{totalVolume.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
